const headingKeys = ["h1", "h2", "h3", "h4", "h5", "h6"];

export const deepMergeTokens = (baseTokens, overrideTokens = {}) => {
    if (!overrideTokens || typeof overrideTokens !== "object") {
        return JSON.parse(JSON.stringify(baseTokens));
    }

    const output = Array.isArray(baseTokens) ? [...baseTokens] : { ...baseTokens };

    Object.keys(overrideTokens).forEach((key) => {
        const baseValue = output[key];
        const overrideValue = overrideTokens[key];

        if (
            baseValue &&
            typeof baseValue === "object" &&
            !Array.isArray(baseValue) &&
            overrideValue &&
            typeof overrideValue === "object" &&
            !Array.isArray(overrideValue)
        ) {
            output[key] = deepMergeTokens(baseValue, overrideValue);
        } else {
            output[key] = overrideValue;
        }
    });

    return output;
};

const buildHeadingScale = (baseFontSize, scale) => {
    return headingKeys.reduce((acc, key, index) => {
        const computed =
            baseFontSize * Math.pow(scale, headingKeys.length - (index + 1));
        acc[key] = `${computed.toFixed(2)}px`;
        return acc;
    }, {});
};

export const buildCssVariables = (tokens) => {
    const { palette, typography, radii, shadows, transitions, layout, spacing } =
        tokens;

    const headingSizes = buildHeadingScale(
        typography.baseFontSize,
        typography.headingScale
    );

    return {
        "--theme-mode": palette.mode,
        "--color-primary": palette.primary,
        "--color-on-primary": palette.onPrimary,
        "--color-secondary": palette.secondary,
        "--color-on-secondary": palette.onSecondary,
        "--color-accent": palette.accent,
        "--color-on-accent": palette.onAccent,
        "--color-surface": palette.surface,
        "--color-surface-muted": palette.surfaceMuted,
        "--color-background": palette.background,
        "--color-border": palette.border,
        "--color-text": palette.text,
        "--color-text-muted": palette.textMuted,
        "--color-success": palette.success,
        "--color-warning": palette.warning,
        "--color-danger": palette.danger,
        "--color-overlay": palette.overlay || "rgba(15, 23, 42, 0.6)",
        "--gradient-hero": palette.gradientHero,
        "--font-family-base": typography.fontFamily,
        "--font-family-heading": typography.headingFontFamily,
        "--font-size-base": `${typography.baseFontSize}px`,
        "--line-height-base": typography.lineHeight,
        "--letter-spacing-base": typography.letterSpacing,
        "--shadow-xs": shadows.xs,
        "--shadow-sm": shadows.sm,
        "--shadow-md": shadows.md,
        "--shadow-lg": shadows.lg,
        "--radius-xs": radii.xs,
        "--radius-sm": radii.sm,
        "--radius-md": radii.md,
        "--radius-lg": radii.lg,
        "--radius-pill": radii.pill,
        "--transition-fast": transitions.fast,
        "--transition-medium": transitions.medium,
        "--transition-slow": transitions.slow,
        "--layout-content-max-width": layout.contentMaxWidth,
        "--layout-sidebar-width": layout.sidebarWidth,
        "--layout-section-spacing": layout.sectionSpacing,
        "--layout-grid-gap": layout.gridGap,
        "--spacing-base": `${spacing.base}px`,
        "--spacing-scale": spacing.scale,
        "--spacing-container": spacing.containerPadding,
        "--spacing-section-y": spacing.sectionPaddingY,
        "--font-size-h1": headingSizes.h1,
        "--font-size-h2": headingSizes.h2,
        "--font-size-h3": headingSizes.h3,
        "--font-size-h4": headingSizes.h4,
        "--font-size-h5": headingSizes.h5,
        "--font-size-h6": headingSizes.h6,
    };
};

export const applyTokensToDocument = (tokens) => {
    if (typeof document === "undefined") {
        return;
    }

    const root = document.documentElement;
    const cssVars = buildCssVariables(tokens);

    Object.entries(cssVars).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });

    root.dataset.themeMode = tokens.palette.mode;

    document.body.style.background = tokens.palette.background;
    document.body.style.color = tokens.palette.text;
    document.body.style.fontFamily = tokens.typography.fontFamily;
    document.body.style.lineHeight = tokens.typography.lineHeight;
};

